import { useCallback, useMemo, useState } from 'react';
import { SIZES_SORT_ORDER } from '@srcDS/components/organisms/productActions/utils/sortOrder';


export const useProductVariant = ({ product, setValue, handleChangeVariant, sizeDefaultLabel = 'Select size' }) => {
  const { projectSpecificData } = product || {};
  const variants = projectSpecificData?.extendedSizes || [];
  const completeTheOutfitProducts = projectSpecificData?.completeTheOutfit || [];


  const firstAvailableVariant = useMemo(
    () => variants.find((item) => item?.projectSpecificData?.availableQuantity > 0) || variants[0] || {},
    [variants],
  );

  const [selectedVariant, setSelectedVariant] = useState(firstAvailableVariant);
  const [activeOptions, setActiveOption] = useState(null);

  const { attributes = {}, projectSpecificData: variantSpecificData = {} } = selectedVariant || {};

  const {
    [`why-we-love-it-01-desc`]: whyWeLoveIt01Desc,
    [`why-we-love-it-02-desc`]: whyWeLoveIt02Desc,
    [`why-we-love-it-01-image`]: whyWeLoveIt01Image,
    [`why-we-love-it-02-image`]: whyWeLoveIt02Image,
    [`why-we-love-it-01-image-url`]: whyWeLoveIt01ImageUrl,
    [`why-we-love-it-02-image-url`]: whyWeLoveIt02ImageUrl,
    [`why-we-love-it-01-title`]: whyWeLoveIt01Title,
    [`why-we-love-it-02-title`]: whyWeLoveIt02Title,
    [`why-we-love-it-title`]: whyWeLoveItTitle,
    finalsale: finalSale,
    finalsalemessage: finalSaleMessage,
  } = attributes;

  // Construct features array from attributes
  const features = useMemo(
    () => [
      ...(whyWeLoveIt01Title && whyWeLoveIt01Desc && whyWeLoveIt01Image
        ? [
            {
              title: whyWeLoveIt01Title,
              text: whyWeLoveIt01Desc,
              image: whyWeLoveIt01Image,
              imageUrl: whyWeLoveIt01ImageUrl,
            },
          ]
        : []),
      ...(whyWeLoveIt02Title && whyWeLoveIt02Desc && whyWeLoveIt02Image
        ? [
            {
              title: whyWeLoveIt02Title,
              text: whyWeLoveIt02Desc,
              image: whyWeLoveIt02Image,
              imageUrl: whyWeLoveIt02ImageUrl,
            },
          ]
        : []),
    ],
    [attributes],
  );


  const productName = useMemo(
    () => selectedVariant?.projectSpecificData?.name || product?.name,
    [selectedVariant, product],
  );

  const productId = useMemo(
    () => selectedVariant?.projectSpecificData?.id || product?.productId,
    [selectedVariant, product],
  );

  const productDescription = useMemo(
    () => selectedVariant?.projectSpecificData?.description || product?.description,
    [selectedVariant, product],
  );

  const styleId = useMemo(
    () => projectSpecificData?.attributes?.find((el) => el?.name === 'stylecode'),
    [projectSpecificData],
  );

  const displayAlphaSize = useMemo(
    () => product?.projectSpecificData?.attributes?.find((el) => el?.name === 'display-alpha-size')?.value,
    [product],
  );


  const isSelectingSize = useMemo(
    () => !activeOptions || !selectedVariant || selectedVariant.projectSpecificData?.availableQuantity <= 0,
    [activeOptions, selectedVariant],
  );

  const variantList = useMemo(() => {
    if (!variants) return null;
    if (!variants[0].attributes?.size?.label) return null;

    let list = [{ label: sizeDefaultLabel, value: '', disabled: true }];

    const data = variants
      .map((variant) => ({
        ...variant,
        label: variant.attributes?.size?.label,
        value: variant.sku,
        isOutOfStock: variant.projectSpecificData?.availableQuantity <= 0,
        availableQuantity: variant.projectSpecificData.availableQuantity,
      }))
      .sort((a, b) => {
        return SIZES_SORT_ORDER.indexOf(a.label) - SIZES_SORT_ORDER.indexOf(b.label);
      });

    list = [...list, ...data];
    return list;
  }, [variants, selectedVariant]);


  const getVariantFromSku = useCallback(() => {
    if (typeof window === 'undefined') return;

    const urlParameters = new URLSearchParams(window.location.search);
    const variantSku = urlParameters.get('sku');

    if (variantSku === null) return;

    const urlVariant =
      variants.find(
        (v) => v?.projectSpecificData?.key === variantSku && v?.projectSpecificData?.availableQuantity > 0,
      ) ||
      variants.find((v) => v?.projectSpecificData?.availableQuantity > 0) ||
      variants[0] ||
      {};

    if (urlVariant) {
      setValue('size1', urlVariant?.sku);
      setValue('size2', urlVariant?.sku);
      setValue('size3', urlVariant?.sku);
      handleChangeVariant(urlVariant);
      setSelectedVariant(urlVariant);
      setActiveOption(urlVariant);
    }

    return urlVariant;
  }, [variants, setValue, handleChangeVariant]);

  const hasQuoteText = !!attributes || !!attributes.productquote;

  return {
    variants,
    selectedVariant,
    setSelectedVariant,
    activeOptions,
    setActiveOption,
    isSelectingSize,
    firstAvailableVariant,
    productName,
    productId,
    productDescription,
    attributes,
    variantList,
    features,
    styleId,
    displayAlphaSize,
    whyWeLoveItTitle,
    finalSale,
    finalSaleMessage,
    completeTheOutfitProducts,
    getVariantFromSku,
    hasQuoteText
  };
};
